import React, { Suspense } from "react"
import { FileGallery } from "core/file-gallery"
import Label from "@kit/design/label"
import { useToggle } from "hooks/use-toggle"
import Icon from "@kit/design/icon"
import { OldButton, OldIconButton } from "@kit/design/old-button"
import Input from "@kit/design/input"
import FileKit from "@convertkit/file-kit"
import { useCurrentUser } from "hooks/use-current-user"

const DokaModal = React.lazy(() => import("vendor/lazy-doka.js"))

const usingPlaceholder = url => url.startsWith("https://cdn.convertkit.com")

export function BlocksImageUploader({
  name,
  label,
  image,
  alt,
  update,
  remove
}) {
  const [fileGalleryOpen, toggleFileGallery] = useToggle(false)
  const user = useCurrentUser()

  function close(file) {
    toggleFileGallery()
    if (file?.dataURL) update({ image: file.dataURL, alt: alt })
  }

  function handleRemove(e) {
    e.stopPropagation()
    remove()
  }

  const handleEditConfirm = async output => {
    const filekit = FileKit({
      authURL: "/filekit/sessions",
      filekitURL: process.env.FILEKIT_HOST,
      id: user.account_id
    })
    const result = await filekit.upload(output.file)

    update({ image: result.original_url, alt: alt })
  }

  return (
    <div>
      <Label>{label}</Label>

      <div className="mt-2">
        {image ? (
          <ImagePreview
            image={image}
            alt={alt}
            remove={handleRemove}
            handleEditConfirm={handleEditConfirm}
            update={update}
            name={name}
            toggleFileGallery={toggleFileGallery}
          />
        ) : (
          <BlankPreview name={name} onClick={toggleFileGallery} />
        )}

        <FileGallery
          active={fileGalleryOpen}
          onClose={close}
          allowedFileTypes={["image"]}
        />
      </div>
    </div>
  )
}

function ImageEditorButton({ src, onChange }) {
  const [active, toggle] = useToggle()

  const handleChange = output => {
    onChange(output)
    toggle(false)
  }

  if (!src) return null

  return (
    <>
      <OldButton
        is="secondary"
        className="w-full"
        onClick={e => {
          e.stopPropagation()
          toggle()
        }}
        icon="edit"
      >
        Edit image
      </OldButton>
      <Suspense fallback={null}>
        {active && (
          <DokaModal
            style={{ maxWidth: "200px", height: "200px" }}
            src={src}
            onConfirm={handleChange}
            onCancel={toggle}
          />
        )}
      </Suspense>
    </>
  )
}

function ImagePreview({
  image,
  alt,
  remove,
  handleEditConfirm,
  update,
  toggleFileGallery
}) {
  return (
    <div className="relative flex">
      <div className="relative">
        <button onClick={toggleFileGallery} data-image-uploader={name}>
          {/* Image Preview */}
          <div className="flex-0 relative" style={{ width: "84px" }}>
            <div
              style={{ backgroundImage: `url(${image})` }}
              className="w-full h-20 bg-gray-50 bg-cover bg-center rounded flex items-center justify-center"
            />
          </div>
        </button>

        {/* Remove Button */}
        <OldIconButton
          is="secondary"
          icon="close"
          onClick={remove}
          className="absolute top-0 right-0 rounded-full -mt-3 -mr-3 shadow hover:text-red-600 transition-colors ease-in-out duration-200"
        />
      </div>
      <div className="flex-1 pl-4">
        {!usingPlaceholder(image) && (
          <div className="mb-2">
            <ImageEditorButton src={image} onChange={handleEditConfirm} />
          </div>
        )}

        <Input
          aria-label="Alt"
          name="alt"
          placeholder="Alt text"
          onChange={e => {
            update({ alt: e.target.value })
          }}
          value={alt || ""}
        />
      </div>
    </div>
  )
}

function BlankPreview({ onClick, name }) {
  return (
    <button
      onClick={onClick}
      className="block w-full"
      data-image-uploader={name}
    >
      <div className="w-full h-20 flex items-center justify-center border-2 border-dashed border-gray-100 rounded text-center text-gray-500 hover:border-gray-700 hover:text-gray-800 transition-colors duration-300 ease-in-out">
        <Icon name="cloud-upload" />
      </div>
    </button>
  )
}
